"use client";

import * as React from "react";
import { cn } from "../cn";
import { Button } from "./button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./dialog";
import { InfoTip } from "./info-tip";

// MODALS (Andrei, 2026-10-27: "all these shouldn't be pages, all this should be modals that you
// open... do the thing and then you close it and you stay here"): the one pattern for adding or
// editing a small record where the person already is. A form in a Dialog: Enter submits, Esc
// closes, and focus goes back to the button that opened it. The caller keeps its view as it was
// (scroll, selection, an open drawer) and only refreshes its data and shows a toast on save.

export type QuickAddDialogProps = {
  /** Short: "New responsibility", "Add to a team". */
  title: string;
  /** What it means, behind the (i) beside the title. */
  info?: React.ReactNode;
  /** One short line under the title (e.g. the seat it is for). */
  description?: React.ReactNode;
  /** The submit button's label: "Add responsibility". */
  submitLabel: string;
  /** While saving; the submit button reads "Saving…" and is disabled. */
  pending?: boolean;
  /** False keeps the submit button disabled (a required field is empty). */
  canSubmit?: boolean;
  /** Runs on submit (button or Enter) when `canSubmit` and not `pending`. */
  onSubmit: () => void;
  /** Closes the dialog (Cancel, Esc, the close button, a click outside). */
  onClose: () => void;
  /** An error under the fields (e.g. a FormError). */
  error?: React.ReactNode;
  /** The form's fields. */
  children: React.ReactNode;
  /** Wider for a form with a table or two columns. */
  size?: "default" | "lg";
  className?: string;
  /** A data-slot on the form, for tests and styling. */
  slot?: string;
};

/**
 * A form in a Dialog for a quick add or edit. Mount it while open (`{adding ? <QuickAddDialog …/>
 * : null}`); it remembers the element that had focus when it opened and returns focus there on
 * close.
 */
export function QuickAddDialog({
  title,
  info,
  description,
  submitLabel,
  pending = false,
  canSubmit = true,
  onSubmit,
  onClose,
  error,
  children,
  size = "default",
  className,
  slot = "quick-add-dialog",
}: QuickAddDialogProps) {
  const opener = useOpener();
  const titleId = React.useId();
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        className={cn(size === "lg" && "sm:max-w-[720px]", className)}
        finalFocus={opener}
      >
        <form
          className="flex flex-col gap-5"
          aria-labelledby={titleId}
          data-slot={slot}
          noValidate
          onSubmit={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (canSubmit && !pending) onSubmit();
          }}
        >
          <DialogHeader>
            <span className="flex items-center gap-1.5 pr-8">
              <DialogTitle id={titleId} className="pr-0">
                {title}
              </DialogTitle>
              {info ? <InfoTip label={`About ${title.toLowerCase()}`}>{info}</InfoTip> : null}
            </span>
            {description ? <DialogDescription>{description}</DialogDescription> : null}
          </DialogHeader>
          {children}
          {error}
          <DialogFooter>
            <Button type="button" variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={!canSubmit || pending}>
              {pending ? "Saving…" : submitLabel}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/**
 * The element that had focus when the dialog mounted (the button that opened it), as a ref for
 * the popup's `finalFocus`; it is also focused again when the dialog unmounts, since a dialog
 * mounted only while open leaves before its close transition can return focus. Exported for a
 * quick add that is not a `QuickAddDialog` (the KPI editor) to return focus the same way.
 */
export function useReturnFocus(): React.RefObject<HTMLElement | null> {
  return useOpener();
}

function useOpener(): React.RefObject<HTMLElement | null> {
  const ref = React.useRef<HTMLElement | null>(null);
  if (ref.current === null && typeof document !== "undefined") {
    const el = document.activeElement;
    if (el instanceof HTMLElement && el !== document.body) ref.current = el;
  }
  // A restore scheduled by a cleanup that was not a real unmount (StrictMode mounts, cleans up
  // and mounts again) is cancelled when the effect runs again.
  const pending = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  React.useEffect(() => {
    if (pending.current !== null) {
      clearTimeout(pending.current);
      pending.current = null;
    }
    const el = ref.current;
    return () => {
      if (!el) return;
      // After the dialog's own focus handling has run.
      pending.current = setTimeout(() => {
        pending.current = null;
        // Another dialog took over (one replaced this one): focus stays with it.
        const active = document.activeElement;
        if (active instanceof HTMLElement && active.closest('[role="dialog"],[role="alertdialog"]'))
          return;
        if (el.isConnected) el.focus({ preventScroll: true });
      }, 0);
    };
  }, []);
  return ref;
}
